import axios from 'axios';

import { ApplicationDto, ApplicationWithRenterDto } from '@tallo/applications';

import { ApplicationBundleDto } from '../interfaces/application-bundle.interfaces';
import { PropertyAttestationsDto } from '../interfaces/property-attestations.interface';
import { SendApplicationRequestDto } from '../interfaces/send-application-request.interface';

class ApplicationsService {
  async getBundles() {
    const response = await axios.get<ApplicationBundleDto[]>(`investor-application/application-bundles`);

    return response.data;
  }

  async getBundlesByProperty(propertyId: string) {
    const response = await axios.get<ApplicationBundleDto[]>(
      `investor-application/property/${propertyId}/application-bundles`,
    );

    return response.data;
  }

  async getBundlesByRenterId(renterId: string) {
    const response = await axios.get<ApplicationBundleDto[]>(
      `investor-application/application-bundles/renter/${renterId}`,
    );

    return response.data;
  }

  async getApplicationsByBundleId(bundleId: string) {
    const response = await axios.get<ApplicationWithRenterDto[]>(
      `investor-application/application-bundle/${bundleId}/applications`,
    );

    return response.data;
  }

  /** Answers with the bundle it just created — the row that tells the UI the application is out. */
  async send(inquiryId: string, requestData?: SendApplicationRequestDto): Promise<ApplicationBundleDto> {
    const response = await axios.post<ApplicationBundleDto>(`investor-application/${inquiryId}`, requestData);

    return response.data;
  }

  async getReport(applicationId: string, productType: string) {
    return axios.get<string>(`investor-application/${applicationId}/report`, { params: { productType } });
  }

  /** Reports the API marks as `pdf` (RentPrep background report); returns an object URL for the viewer. */
  async getReportPdf(applicationId: string, productType: string): Promise<string> {
    const response = await axios.get<Blob>(`investor-application/${applicationId}/report/pdf`, {
      params: { productType },
      responseType: 'blob',
    });

    return URL.createObjectURL(response.data);
  }

  async resend(applicationBundleId: string) {
    return axios.post<ApplicationBundleDto[]>(`investor-application/${applicationBundleId}/resend`);
  }

  async accept(applicationBundleId: string): Promise<ApplicationBundleDto> {
    const response = await axios.post<ApplicationBundleDto>(`investor-application/${applicationBundleId}/accept`);
    return response.data;
  }

  async decline(applicationBundleId: string): Promise<ApplicationBundleDto> {
    const response = await axios.post<ApplicationBundleDto>(`investor-application/${applicationBundleId}/decline`);
    return response.data;
  }

  /** Fair-Housing-screened on the BE: a 406 carries the reason the message was rejected. */
  async requestUpdate(applicationBundleId: string, message: string): Promise<ApplicationBundleDto> {
    const response = await axios.post<ApplicationBundleDto>(
      `investor-application/${applicationBundleId}/request-update`,
      { message },
    );
    return response.data;
  }

  async getPropertyAttestations(propertyId: string): Promise<PropertyAttestationsDto> {
    const response = await axios.get<PropertyAttestationsDto>(
      `investor-application/property/${propertyId}/attestations`,
    );
    return response.data;
  }

  /**
   * Records the landlord's confirmation that they've made a non-criminal
   * pre-qualification decision on a property in a bifurcated-screening
   * jurisdiction (NYC, Cook County IL, etc.). Idempotent on the BE.
   */
  async approveCriminalScreening(applicationId: string): Promise<ApplicationDto> {
    const response = await axios.post<ApplicationDto>(
      `investor-application/${applicationId}/approve-criminal-screening`,
    );
    return response.data;
  }
}

export default new ApplicationsService();
